<template>
    <div class="container">
        <div class="pt-5">
            <h1 class="h3">Settings</h1>

            <div class="py-2">
                <settings-menu></settings-menu>
            </div>

            <div class="row py-1 border-bottom">
                <div class="col-md-6 col-sm-12">
                    <h3 class="h3 text-primary">Payments & Taxes</h3>
                    <small>View and update your payments and Taxes details</small>
                </div>
                <div class="col-md-6 col-sm-12">
                    <div class="float-right">
                        <button @click="saveSettingsDetails" class="btn btn-primary px-3">
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>

            <div class="container text-left mb-5">
                <!-- Add Payment Modal -->
                <b-modal ref="paymentModal" id="payment-modal" hide-footer static>
                    <h4 class="text-center py-2">{{ paymentForm.id ? 'Edit' : 'New' }} Payment Method</h4>
                    <form @submit.prevent="submitPaymentForm">
                        <div class="form-group">
                            <label>Method name</label>
                            <input class="form-control" v-model="paymentForm.payment_method" required />
                        </div>
                        <div class="form-group">
                            <label>Brief description</label>
                            <textarea class="form-control" v-model="paymentForm.description"></textarea>
                        </div>
                        <div class="form-group">
                            <label>Account / Till / Paybill Number <small class="text-muted">(optional)</small></label>
                            <input class="form-control" v-model="paymentForm.account_number" placeholder="e.g. 8881707, 247247" />
                            <small class="form-text text-muted">
                                Used to automatically match incoming SMS payments to this method.
                                Enter the number exactly as it appears in your bank/M-Pesa SMS.
                            </small>
                        </div>
                        <div class="form-group">
                            <label>Account Label <small class="text-muted">(optional)</small></label>
                            <input class="form-control" v-model="paymentForm.account_label" placeholder="e.g. Main Till, KCB Current" />
                            <small class="form-text text-muted">Friendly name shown in reports when this account is matched.</small>
                        </div>
                        <div class="form-group d-flex justify-content-end">
                            <button type="button" class="btn btn-light mr-2" @click="closePaymentModal">Cancel</button>
                            <button type="submit" class="btn btn-primary">{{ paymentForm.id ? 'Update' : 'Create' }}</button>
                        </div>
                    </form>
                </b-modal>

                <!-- Add Tax Modal -->
                <b-modal ref="taxModal" id="tax-modal" hide-footer static>
                    <h4 class="text-center py-2">{{ taxForm.id ? 'Edit' : 'New' }} Tax / Levy</h4>
                    <form @submit.prevent="submitTaxForm">
                        <div class="form-group">
                            <label>Tax name</label>
                            <input class="form-control" v-model="taxForm.tax" required />
                        </div>

                        <div class="form-group">
                            <label>Rate (%)</label>
                            <input class="form-control" type="number" step="0.01" v-model.number="taxForm.tax_perc" required />
                        </div>

                        <div class="form-group">
                            <label>Description (optional)</label>
                            <textarea class="form-control" v-model="taxForm.description"></textarea>
                        </div>
                        <div class="form-group">
                            <label>Show this tax on customer receipt</label>
                            <input class="form-check" type="checkbox" v-model="taxForm.on_receipt" />
                        </div>

                        <div class="form-group d-flex justify-content-end">
                            <button type="button" class="btn btn-light mr-2" @click="closeTaxModal">Cancel</button>
                            <button type="submit" class="btn btn-primary">{{ taxForm.id ? 'Update' : 'Create' }}</button>
                        </div>
                    </form>
                </b-modal>

                <!-- Create Other Payments -->
                <div class="row py-1 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">You can create other payment methods for your business</h6>
                        <small>Example: bank transfer, cheque, custom mobile wallets, etc.</small>
                    </div>
                    <div class="col-md-6 col-sm-12 text-right">
                        <button class="btn btn-outline-primary" @click="openNewPayment">Add Payment Method</button>
                    </div>
                </div>

                <!-- Dynamic Payment Methods (unified list: core + customs) -->
                <div v-for="method in paymentMethods" :key="method.id" class="row py-1 border-bottom align-items-center">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1 mb-0">{{ method.name }}</h6>
                        <small class="text-muted">{{ method.description }}</small>
                        <span v-if="method.account_number" class="badge badge-light ml-1">
                            {{ method.account_label || method.account_number }}
                        </span>
                    </div>

                    <div class="col-md-6 col-sm-12 d-flex justify-content-end align-items-center">
                        <div class="custom-control custom-switch mr-3">
                            <input type="checkbox"
                                   class="custom-control-input"
                                   :id="'pm-'+method.id"
                                   v-model="method.enabled"
                                   @change="togglePaymentEnabled(method)">
                            <label class="custom-control-label" :for="'pm-'+method.id"><small>Enabled</small></label>
                        </div>

                        <button class="btn btn-sm btn-light mr-2" @click="openEditPayment(method)">Edit</button>
                        <button class="btn btn-sm btn-outline-danger" @click="confirmDeletePayment(method)"><i class="fa fa-trash"></i></button>
                    </div>
                </div>

                <!-- Built-in Payments related settings (keeps mpesa config etc.) -->
                <div class="row py-1 border-bottom mt-4">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Setup M-Pesa <small class="text-danger">*</small></h6>
                        <small>Choose Paybill/Till Number to use</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" v-model="paymentDetails.useDukakitShortcode" id="mpesa-switch">
                            <label class="custom-control-label" for="mpesa-switch"><small>We have our own Paybill/Till</small></label>
                        </div>
                    </div>
                </div>

                <div v-if="paymentDetails.useDukakitShortcode" class="row py-1 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Paybill or Till? <small class="text-danger">*</small></h6>
                        <small>Customers pay a Paybill with an account number; a Till (Buy Goods) without one.</small>
                    </div>
                    <div class="col-md-6 col-sm-12 pt-2">
                        <div class="custom-control custom-radio custom-control-inline">
                            <input type="radio" id="mpesa-type-paybill" class="custom-control-input" value="paybill" v-model="paymentDetails.mpesaDetails.account_type">
                            <label class="custom-control-label" for="mpesa-type-paybill">Paybill</label>
                        </div>
                        <div class="custom-control custom-radio custom-control-inline">
                            <input type="radio" id="mpesa-type-till" class="custom-control-input" value="till" v-model="paymentDetails.mpesaDetails.account_type">
                            <label class="custom-control-label" for="mpesa-type-till">Till (Buy Goods)</label>
                        </div>
                    </div>
                </div>

                <div v-if="paymentDetails.useDukakitShortcode" class="row py-1 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">{{ paymentDetails.mpesaDetails.account_type === 'till' ? 'Store Number' : 'Paybill Number' }} <small class="text-danger">*</small></h6>
                        <small v-if="paymentDetails.mpesaDetails.account_type === 'till'">The Till's <strong>Store (head office) number</strong> — the shortcode your Daraja app went live for. Not the till number customers see.</small>
                        <small v-else>Your Paybill number. Incoming payments are matched to your business by this number.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <input class="form-control" v-model="paymentDetails.mpesaDetails.shortcode" />
                    </div>
                </div>

                <div v-if="paymentDetails.useDukakitShortcode && paymentDetails.mpesaDetails.account_type === 'till'" class="row py-1 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Till Number <small class="text-danger">*</small></h6>
                        <small>The Buy Goods number customers pay to. M-Pesa prompts send the money here.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <input class="form-control" v-model="paymentDetails.mpesaDetails.till_number" />
                    </div>
                </div>

                <div v-if="paymentDetails.useDukakitShortcode" class="row py-1 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Integration <small class="text-danger">*</small></h6>
                        <small>You can leave this as is if you verify payments manually.</small>
                        <p>
                            <small>
                                Only proceed if you are confident in the process; otherwise,
                                <a :href="'https://api.whatsapp.com/send/?phone=254755133133&text=Hello%20Team%20' + $platformBrand.name + ',%20I%20need%20assistance%20on%20Mpesa%20Integration,%20c-id='">
                                    contact us on WhatsApp for assistance
                                </a>.
                            </small>
                        </p>
                    </div>
                    <div class="col-md-6 col-sm-12 d-flex align-items-center">
                        <div class="custom-control custom-switch mr-3">
                            <input type="checkbox" class="custom-control-input" v-model="paymentDetails.mpesaDetails.haveApiKeys" id="mpesa-integrate">
                            <label class="custom-control-label" for="mpesa-integrate"><small>I know how to integrate</small></label>
                        </div>
                    </div>
                </div>

                <!-- Mpesa API Configs -->
                <div v-if="paymentDetails.mpesaDetails.haveApiKeys">
                    <div v-if="paymentDetails.mpesaDetails.keys_unreadable" class="alert alert-danger my-2 mb-0">
                        Your saved M-Pesa keys could not be read. Re-enter the Consumer Key, Consumer Secret and Keyphrase below and save.
                    </div>
                    <div class="row py-1 border-bottom">
                        <div class="col-md-6 col-sm-12">
                            <h6 class="py-1">Consumer Secret <small class="text-danger">*</small></h6>
                            <small>Get this from your Safaricom Daraja portal</small>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <input class="form-control" v-model="paymentDetails.mpesaDetails.consumer_secret" />
                        </div>
                    </div>

                    <div class="row py-1 border-bottom">
                        <div class="col-md-6 col-sm-12">
                            <h6 class="py-1">Consumer Key <small class="text-danger">*</small></h6>
                            <small>Get this from your Safaricom Daraja portal</small>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <input class="form-control" v-model="paymentDetails.mpesaDetails.consumer_key" />
                        </div>
                    </div>

                    <div class="row py-1 border-bottom">
                        <div class="col-md-6 col-sm-12">
                            <h6 class="py-1">Keyphrase <small class="text-danger">*</small></h6>
                            <small>Shared on email by Safaricom</small>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <input class="form-control" v-model="paymentDetails.mpesaDetails.keyphrase" />
                        </div>
                    </div>

                </div>

                <!-- Direct Paybill payments (C2B) -->
                <div v-if="paymentDetails.useDukakitShortcode" class="row py-2 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Receive direct Paybill payments</h6>
                        <small class="text-muted">
                            Save your number and API keys above, then press <strong>Register with Safaricom</strong> - Safaricom only accepts these URLs through its API, not the Daraja portal.
                            Customers who pay your Paybill or Till directly will then appear under Payment Transactions, ready to match to orders - same as SMS validation.
                        </small>
                        <p class="mb-0 mt-1">
                            <small class="text-muted">This replaces any URLs already registered on that Paybill/Till, so check that no other system uses it first.</small>
                        </p>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <template v-if="paymentDetails.mpesaDetails.c2b_confirmation_url">
                            <label class="mb-1"><small>Confirmation URL</small></label>
                            <div class="input-group mb-2">
                                <input class="form-control" readonly style="cursor:pointer" title="Click to copy" :value="paymentDetails.mpesaDetails.c2b_confirmation_url" @click="copyText(paymentDetails.mpesaDetails.c2b_confirmation_url)" />
                                <div class="input-group-append">
                                    <button class="btn btn-outline-primary" type="button" @click="copyText(paymentDetails.mpesaDetails.c2b_confirmation_url)"><i class="bi bi-clipboard"></i></button>
                                </div>
                            </div>
                            <label class="mb-1"><small>Validation URL</small></label>
                            <div class="input-group">
                                <input class="form-control" readonly style="cursor:pointer" title="Click to copy" :value="paymentDetails.mpesaDetails.c2b_validation_url" @click="copyText(paymentDetails.mpesaDetails.c2b_validation_url)" />
                                <div class="input-group-append">
                                    <button class="btn btn-outline-primary" type="button" @click="copyText(paymentDetails.mpesaDetails.c2b_validation_url)"><i class="bi bi-clipboard"></i></button>
                                </div>
                            </div>
                            <button type="button" class="btn btn-primary btn-block mt-3" :disabled="registeringC2b" @click="registerC2bUrls">
                                <span v-if="registeringC2b" class="spinner-border spinner-border-sm mr-1"></span>
                                Register with Safaricom
                            </button>
                            <small v-if="c2bRegisterResult" class="d-block mt-1" :class="c2bRegisterResult.ok ? 'text-primary' : 'text-danger'">{{ c2bRegisterResult.message }}</small>
                        </template>
                    </div>
                </div>

                <!-- Dukakit Payments Section -->
                <div class="row py-1 border-bottom mt-4">
                    <div class="col-12">
                        <h5 class="py-1">{{ $platformBrand.name }} Payments</h5>
                        <small class="text-muted">Accept customer payments through {{ $platformBrand.name }} via STK Push or Paybill, and manage your {{ $platformBrand.name }} wallet.</small>
                    </div>
                </div>

                <div class="row py-2 border-bottom align-items-center">
                    <div class="col-md-8 col-sm-12">
                        <h6 class="py-1 mb-0">Receive Payments through {{ $platformBrand.name }}</h6>
                        <small class="text-muted">Customers can pay via M-Pesa STK Push or Paybill {{ dukakitPayments.paybill }}, routed straight to your {{ $platformBrand.name }} wallet.</small>
                    </div>
                    <div class="col-md-4 col-sm-12 text-right">
                        <div class="custom-control custom-switch">
                            <input
                                type="checkbox"
                                class="custom-control-input"
                                id="enable-dukakit-payments"
                                v-model="dukakitPayments.enabled"
                                @change="toggleDukakitPayments"
                            />
                            <label class="custom-control-label" for="enable-dukakit-payments"><small>Enabled</small></label>
                        </div>
                    </div>
                </div>

                <div v-if="dukakitPayments.enabled">
                    <div class="row py-2 border-bottom align-items-center">
                        <div class="col-md-6 col-sm-12">
                            <h6 class="py-1 mb-0">{{ $platformBrand.name }} Account</h6>
                            <small class="text-muted">Share this with customers for manual Paybill payments.</small>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <template v-if="dukakitPayments.account_number">
                                <div class="d-flex justify-content-end">
                                    <span class="badge badge-light mr-2 p-2">Paybill: {{ dukakitPayments.paybill }}</span>
                                    <span class="badge badge-primary p-2">Account: {{ dukakitPayments.account_number }}</span>
                                </div>
                            </template>
                            <template v-else>
                                <div class="d-flex justify-content-end">
                                    <input class="form-control mr-2" style="max-width:160px" maxlength="6" placeholder="Preferred 6-digit no. (optional)" v-model="accountNumberPreference" />
                                    <button class="btn btn-outline-primary" @click="requestAccountNumber">Get Account Number</button>
                                </div>
                                <small v-if="accountNumberSuggestions.length" class="text-muted d-block text-right mt-1">
                                    That number is taken. Try: {{ accountNumberSuggestions.join(', ') }}
                                </small>
                            </template>
                        </div>
                    </div>

                    <div class="row py-2 border-bottom align-items-center">
                        <div class="col-md-6 col-sm-12">
                            <h6 class="py-1 mb-0">Settlement Account</h6>
                            <small class="text-muted">Where your net proceeds are paid out after fees. Can be a phone number, a Paybill, or a Till (Buy Goods).</small>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <template v-if="dukakitPayments.settlement_account && dukakitPayments.settlement_account.locked">
                                <div class="text-right">
                                    <span class="badge badge-light p-2">{{ settlementTypeLabel(dukakitPayments.settlement_account.destination_type) }}: {{ dukakitPayments.settlement_account.masked_identifier }}</span>
                                    <div><small class="text-danger">Locked — contact {{ $platformBrand.name }} Support to change.</small></div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="text-right">
                                    <select class="form-control mb-2" v-model="settlementAccountForm.destination_type" @change="settlementIdentifierValidMessage = ''">
                                        <option value="mpesa_number">M-Pesa Phone Number</option>
                                        <option value="paybill">Paybill</option>
                                        <option value="till">Till (Buy Goods)</option>
                                    </select>
                                    <div class="mb-2" v-if="settlementAccountForm.destination_type === 'mpesa_number'">
                                        <vue-tel-input
                                            v-on:validate="validateSettlementIdentifier"
                                            v-model="settlementAccountForm.account_identifier"
                                        ></vue-tel-input>
                                        <small v-if="settlementIdentifierValidMessage" class="text-danger d-block mt-1">{{ settlementIdentifierValidMessage }}</small>
                                    </div>
                                    <div class="d-flex mb-2" v-else>
                                        <input
                                            class="form-control mr-2"
                                            :placeholder="settlementIdentifierPlaceholder"
                                            v-model="settlementAccountForm.account_identifier"
                                        />
                                        <input
                                            class="form-control"
                                            style="max-width:140px"
                                            placeholder="Account ref"
                                            maxlength="13"
                                            v-model="settlementAccountForm.account_reference"
                                        />
                                    </div>
                                    <button class="btn btn-outline-primary" :disabled="settlementAccountForm.destination_type === 'mpesa_number' && !!settlementIdentifierValidMessage" @click="setupSettlementAccount">Save &amp; Lock</button>
                                </div>
                            </template>
                        </div>
                    </div>

                    <div class="row py-2 border-bottom align-items-center">
                        <div class="col-md-6 col-sm-12">
                            <h6 class="py-1 mb-0">Settlement Frequency</h6>
                            <small class="text-muted">When collected payments are paid out to your settlement account.</small>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <select class="form-control" v-model="dukakitPayments.settlement_frequency" @change="saveSettlementFrequency">
                                <option value="immediate">Immediately</option>
                                <option value="daily">Daily at midnight</option>
                            </select>
                        </div>
                    </div>

                    <div class="row py-2 border-bottom align-items-center">
                        <div class="col-12 text-right">
                            <small class="text-muted">{{ $platformBrand.name }} Wallet Balance: <strong>KSh {{ dukakitPayments.balance }}</strong></small>
                            &nbsp;·&nbsp;
                            <a href="/send-receive-money">Go to Send &amp; Receive Money &rarr;</a>
                        </div>
                    </div>
                </div>

                <!-- Opening Cash Section -->
                <div class="row py-1 border-bottom mt-4">
                    <div class="col-12">
                        <h5 class="py-1">Opening Cash / Cash Session</h5>
                        <small class="text-muted">Control whether cashiers must declare an opening float before processing transactions.</small>
                    </div>
                </div>

                <div class="row py-2 border-bottom align-items-center">
                    <div class="col-md-8 col-sm-12">
                        <h6 class="py-1 mb-0">Enable Opening Cash</h6>
                        <small class="text-muted">Require cashiers to open a cash session with a float before any financial operation.</small>
                    </div>
                    <div class="col-md-4 col-sm-12 text-right">
                        <div class="custom-control custom-switch">
                            <input
                                type="checkbox"
                                class="custom-control-input"
                                id="enable-opening-cash"
                                v-model="openingCashSettings.enable_opening_cash"
                            />
                            <label class="custom-control-label" for="enable-opening-cash"><small>Enabled</small></label>
                        </div>
                    </div>
                </div>

                <div class="row py-2 border-bottom align-items-center" v-if="openingCashSettings.enable_opening_cash">
                    <div class="col-md-8 col-sm-12">
                        <h6 class="py-1 mb-0">Block sales until cash session is open</h6>
                        <small class="text-muted">Prevent any sale or payment from going through without an active cash session.</small>
                    </div>
                    <div class="col-md-4 col-sm-12 text-right">
                        <div class="custom-control custom-switch">
                            <input
                                type="checkbox"
                                class="custom-control-input"
                                id="require-opening-cash"
                                v-model="openingCashSettings.require_opening_cash_before_sales"
                            />
                            <label class="custom-control-label" for="require-opening-cash"><small>Required</small></label>
                        </div>
                    </div>
                </div>

                <!-- Shift Cash Closing (only visible when enable_shifts is on) -->
                <div v-if="enableShifts" class="row py-2 border-bottom align-items-center">
                    <div class="col-md-8 col-sm-12">
                        <h6 class="py-1 mb-0">Enable Shift Cash Closing</h6>
                        <small class="text-muted">
                            Require cashiers to submit a cash count at the end of each shift for manager approval.
                            Tracks variance between expected and actual cash in the till.
                        </small>
                    </div>
                    <div class="col-md-4 col-sm-12 text-right">
                        <div class="custom-control custom-switch">
                            <input
                                type="checkbox"
                                class="custom-control-input"
                                id="enable-shift-cash-closing"
                                v-model="openingCashSettings.enable_shift_cash_closing"
                            />
                            <label class="custom-control-label" for="enable-shift-cash-closing"><small>Enabled</small></label>
                        </div>
                    </div>
                </div>

                <!-- Payment Validation Section -->
                <div class="row py-2 border-bottom align-items-center">
                    <div class="col-md-8 col-sm-12">
                        <h6 class="py-1 mb-0">SMS Payment Validation</h6>
                        <small class="text-muted">
                            Require all non-cash payments to be confirmed against an incoming M-Pesa SMS before checkout completes.
                            Needs the <strong>Validator App</strong> installed on the business phone.
                        </small>
                    </div>
                    <div class="col-md-4 col-sm-12 text-right">
                        <div class="custom-control custom-switch">
                            <input
                                type="checkbox"
                                class="custom-control-input"
                                id="enable-payment-validation"
                                v-model="paymentValidationSettings.enable_payment_validation"
                            />
                            <label class="custom-control-label" for="enable-payment-validation"><small>Enabled</small></label>
                        </div>
                    </div>
                </div>

                <!-- Skip Validation Sub-setting (only when validation is on) -->
                <div v-if="paymentValidationSettings.enable_payment_validation" class="row py-2 border-bottom align-items-center pl-3">
                    <div class="col-md-8 col-sm-12">
                        <h6 class="py-1 mb-0 text-secondary">
                            <i class="bi bi-arrow-return-right mr-1"></i>Allow All Employees to Skip
                        </h6>
                        <small class="text-muted">
                            When <strong>on</strong>, any employee can skip validation without supervisor approval (logged).
                            When <strong>off</strong>, employees see the skip button but must request supervisor authorization to proceed.
                        </small>
                    </div>
                    <div class="col-md-4 col-sm-12 text-right">
                        <div class="custom-control custom-switch">
                            <input
                                type="checkbox"
                                class="custom-control-input"
                                id="allow-all-employees-skip-validation"
                                v-model="paymentValidationSettings.allow_all_employees_skip_validation"
                            />
                            <label class="custom-control-label" for="allow-all-employees-skip-validation"><small>Enabled</small></label>
                        </div>
                    </div>
                </div>

                <!-- Customer Wallet Section -->
                <div class="row py-2 border-bottom align-items-center">
                    <div class="col-md-8 col-sm-12">
                        <h6 class="py-1 mb-0">Customer Wallet</h6>
                        <small class="text-muted">
                            Allow customers to hold a wallet balance that can be used to pay for orders, subscriptions, and reservations.
                            When enabled, a wallet is automatically created for every customer.
                        </small>
                    </div>
                    <div class="col-md-4 col-sm-12 text-right">
                        <div class="custom-control custom-switch">
                            <input
                                type="checkbox"
                                class="custom-control-input"
                                id="enable-customer-wallet"
                                v-model="walletSettings.enable_customer_wallet"
                            />
                            <label class="custom-control-label" for="enable-customer-wallet"><small>Enabled</small></label>
                        </div>
                    </div>
                </div>

                <!-- ETIMS / Tremol Bridge Section (only for companies with ETIMS enabled) -->
                <div v-if="etimsEnabled">
                    <div class="row py-1 border-bottom mt-4">
                        <div class="col-12">
                            <h5 class="py-1">ETIMS / Tremol Bridge</h5>
                            <small class="text-muted">Control when {{ $platformBrand.name }} is allowed to send ETIMS data to the Tremol Bridge. Transactions outside the allowed period will not be queued for later submission.</small>
                        </div>
                    </div>

                    <div class="row py-2 border-bottom">
                        <div class="col-12">
                            <h6 class="py-1 mb-2">Submission</h6>

                            <div class="custom-control custom-radio mb-2">
                                <input type="radio" class="custom-control-input" id="etims-policy-always"
                                       value="always" v-model="etimsSubmissionSettings.etims_submission_policy">
                                <label class="custom-control-label" for="etims-policy-always">Always submit</label>
                            </div>
                            <div class="custom-control custom-radio mb-2">
                                <input type="radio" class="custom-control-input" id="etims-policy-blocked"
                                       value="blocked" v-model="etimsSubmissionSettings.etims_submission_policy">
                                <label class="custom-control-label" for="etims-policy-blocked">Block submissions</label>
                            </div>
                            <div class="custom-control custom-radio mb-2">
                                <input type="radio" class="custom-control-input" id="etims-policy-scheduled"
                                       value="scheduled" v-model="etimsSubmissionSettings.etims_submission_policy">
                                <label class="custom-control-label" for="etims-policy-scheduled">Submit only during allowed hours</label>
                            </div>

                            <small v-if="etimsSubmissionSettings.etims_submission_policy === 'always'" class="text-muted d-block mt-2">
                                {{ $platformBrand.name }} will submit ETIMS data using the normal fiscalization flow.
                            </small>
                            <small v-else-if="etimsSubmissionSettings.etims_submission_policy === 'blocked'" class="text-muted d-block mt-2">
                                ETIMS submissions are currently blocked. {{ $platformBrand.name }} will not send fiscalization data to the Tremol Bridge.
                            </small>
                            <small v-else class="text-muted d-block mt-2">
                                Outside these hours, {{ $platformBrand.name }} will not send fiscalization data to the Tremol Bridge. Transactions are not queued for later submission.
                            </small>
                        </div>
                    </div>

                    <div v-if="etimsSubmissionSettings.etims_submission_policy === 'scheduled'" class="row py-2 border-bottom">
                        <div class="col-md-4 col-sm-12">
                            <label class="mb-1"><small>Allowed from</small></label>
                            <input type="time" class="form-control" v-model="etimsSubmissionSettings.etims_submission_start_time">
                        </div>
                        <div class="col-md-4 col-sm-12">
                            <label class="mb-1"><small>Allowed until</small></label>
                            <input type="time" class="form-control" v-model="etimsSubmissionSettings.etims_submission_end_time">
                        </div>
                        <div class="col-md-4 col-sm-12">
                            <label class="mb-1"><small>Timezone</small></label>
                            <input type="text" class="form-control" :value="etimsSubmissionSettings.etims_submission_timezone" disabled>
                            <small class="text-muted">The schedule above uses this timezone.</small>
                        </div>
                    </div>
                </div>

                <!-- TAXES Section -->
                <div class="row py-1 border-bottom mt-4">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Taxes & Levies</h6>
                        <small>Manage applicable taxes</small>
                    </div>
                    <div class="col-md-6 col-sm-12 text-right">
                        <button class="btn btn-outline-primary" @click="openNewTax">Add Tax / Levy</button>
                    </div>
                </div>

                <!-- Dynamic Taxes -->
                <div v-for="t in taxes" :key="t.id" class="row py-1 border-bottom align-items-center">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1 mb-0">{{ t.tax }} <small class="text-muted">({{ t.tax_perc }}%)</small></h6>
                        <small class="text-muted">{{ t.description }}</small>
                    </div>

                    <div class="col-md-6 col-sm-12 d-flex justify-content-end align-items-center">
                        <div class="custom-control custom-switch mr-3">
                            <input type="checkbox"
                                   class="custom-control-input"
                                   :id="'tx-'+t.id"
                                   v-model="t.enabled"
                                   @change="toggleTaxEnabled(t)">
                            <label class="custom-control-label" :for="'tx-'+t.id"><small>Enabled</small></label>
                        </div>

                        <button class="btn btn-sm btn-light mr-2" @click="openEditTax(t)">Edit</button>
                        <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteTax(t)"><i class="fa fa-trash"></i></button>
                    </div>
                </div>

            </div>
        </div>
        <div class="my-5"></div>
    </div>
</template>

<script>
export default {
    name: 'profile',

    data() {
        return {
            // keep minimal paymentDetails (as requested)
            registeringC2b: false,
            c2bRegisterResult: null,
            paymentDetails: {
                useDukakitShortcode: false,
                mpesaDetails: {
                    shortcode: "",
                    account_type: "paybill",
                    till_number: "",
                    keyphrase: "",
                    consumer_key: "",
                    consumer_secret: "",
                    haveApiKeys: false,
                    keys_unreadable: false
                }
            },

            // Single flat arrays for dynamic lists
            paymentMethods: [],

            // form model for payment modal
            paymentForm: {
                id: null,
                payment_method: "",
                description: "",
                account_number: "",
                account_label: "",
                enabled: true
            },

            taxes: [],

            taxForm: {
                id: null,
                tax: "",
                tax_perc: null,
                description: "",
                enabled: true,
                on_receipt:true
            },

            enableShifts: false,

            openingCashSettings: {
                enable_opening_cash: false,
                require_opening_cash_before_sales: false,
                enable_shift_cash_closing: false,
            },

            paymentValidationSettings: {
                enable_payment_validation: false,
                allow_all_employees_skip_validation: false,
            },

            walletSettings: {
                enable_customer_wallet: false,
            },

            etimsEnabled: false,
            etimsSubmissionSettings: {
                etims_submission_policy: 'always',
                etims_submission_start_time: '08:00',
                etims_submission_end_time: '17:00',
                etims_submission_timezone: 'Africa/Nairobi',
            },

            dukakitPayments: {
                enabled: false,
                account_number: null,
                paybill: '4109900',
                settlement_frequency: 'immediate',
                balance: '0.00',
                settlement_account: null,
            },
            accountNumberPreference: '',
            accountNumberSuggestions: [],
            settlementAccountForm: {
                destination_type: 'mpesa_number',
                account_identifier: '',
                account_reference: '',
            },
            settlementIdentifierValidMessage: '',

            loading: {
                payments: false,
                taxes: false
            }
        };
    },

    computed: {
        settlementIdentifierPlaceholder() {
            return {
                mpesa_number: 'M-Pesa number, e.g. 0712345678',
                paybill: 'Paybill number, e.g. 400200',
                till: 'Till number, e.g. 123456',
            }[this.settlementAccountForm.destination_type] || '';
        },
    },

    mounted() {
        // load everything in parallel
        Promise.all([
            this.getPaymentMethods(),
            this.getTaxes(),
            this.getCompanySettings(),
            this.getMpesaConfigs(),
            this.getDukakitPaymentsStatus()
        ]).catch(e => {
            console.error(e);
        });
    },

    methods: {
        /* -------------------- PAYMENT METHODS CRUD -------------------- */

        getPaymentMethods() {
            this.loading.payments = true;
            return axios.get('/api/payments')
                .then(({ data }) => {
                    // ensure boolean enabled
                    this.paymentMethods = (data || []).map(pm => ({
                        id: pm.id,
                        payment_method: pm.payment_method || pm.name || pm.title || '',
                        name: pm.payment_method || pm.name || pm.title || '',
                        description: pm.description || pm.note || '',
                        account_number: pm.account_number || '',
                        account_label: pm.account_label || '',
                        enabled: !!pm.enabled,
                    }));
                })
                .catch(err => {
                    console.error('getPaymentMethods', err);
                })
                .finally(() => this.loading.payments = false);
        },

        openNewPayment() {
            this.paymentForm = { id: null, payment_method: "", description: "", account_number: "", account_label: "", enabled: true };
            this.$refs.paymentModal.show();
        },

        openEditPayment(method) {
            this.paymentForm = {
                id: method.id,
                payment_method: method.payment_method || method.name || '',
                description: method.description || '',
                account_number: method.account_number || '',
                account_label: method.account_label || '',
                enabled: !!method.enabled,
            };
            this.$refs.paymentModal.show();
        },

        closePaymentModal() {
            this.$refs.paymentModal.hide();
        },

        submitPaymentForm() {
            const payload = {
                payment_method: this.paymentForm.payment_method,
                description: this.paymentForm.description,
                account_number: this.paymentForm.account_number || null,
                account_label: this.paymentForm.account_label || null,
                enabled: !!this.paymentForm.enabled
            };

            if (this.paymentForm.id) {
                axios.put(`/api/payment-methods/${this.paymentForm.id}`, payload)
                    .then(() => {
                        this.$toasts.push({ type: 'success', message: 'Payment method updated' });
                        this.getPaymentMethods();
                        this.closePaymentModal();
                    })
                    .catch(err => console.error(err));
            } else {
                axios.post('/api/payment-methods', payload)
                    .then(() => {
                        this.$toasts.push({ type: 'success', message: 'Payment method created' });
                        this.getPaymentMethods();
                        this.closePaymentModal();
                    })
                    .catch(err => console.error(err));
            }
        },

        togglePaymentEnabled(method) {
            // immediate UI toggle already done by v-model; persist change
            axios.put(`/api/payment-methods/${method.id}`, { enabled: !!method.enabled })
                .then(() => {
                    this.$toasts.push({ type: 'success', message: `${method.name} ${method.enabled ? 'enabled' : 'disabled'}` });
                })
                .catch(err => {
                    console.error(err);
                    // revert on error
                    method.enabled = !method.enabled;
                });
        },

        confirmDeletePayment(method) {
            if (!confirm(`Delete payment method "${method.name}"?`)) return;
            this.deletePaymentMethod(method.id);
        },

        deletePaymentMethod(id) {
            axios.delete(`/api/payment-methods/${id}`)
                .then(() => {
                    this.$toasts.push({ type: 'success', message: 'Payment method deleted' });
                    this.getPaymentMethods();
                })
                .catch(err => console.error(err));
        },

        /* -------------------- TAXES CRUD -------------------- */

        getTaxes() {
            this.loading.taxes = true;
            return axios.get('/api/taxes')
                .then(({ data }) => {
                    this.taxes = data.data.map(t => ({
                        id: t.id,
                        tax: t.tax,
                        tax_perc: Number(t.tax_perc),
                        enabled: !!t.enabled,
                        on_receipt: !!t.on_receipt
                    }));
                })
                .catch(err => console.error('getTaxes', err))
                .finally(() => this.loading.taxes = false);
        },

        openNewTax() {
            this.taxForm = { id: null, name: "", rate: null, description: "", enabled: true, on_receipt: true };
            this.$refs.taxModal.show();
        },

        openEditTax(t) {
            this.taxForm = { ...t };
            this.$refs.taxModal.show();
        },

        closeTaxModal() {
            this.$refs.taxModal.hide();
        },

       submitTaxForm() {
    const payload = {
        tax: this.taxForm.tax,
        tax_perc: this.taxForm.tax_perc,
        description: this.taxForm.description,
        enabled: !!this.taxForm.enabled,
        on_receipt: !!this.taxForm.on_receipt
    };

    if (this.taxForm.id) {
        axios.put(`/api/taxes/${this.taxForm.id}`, payload)
            .then(() => {
                this.$toasts.push({ type: 'success', message: 'Tax updated' });
                this.getTaxes();
                this.closeTaxModal();
            })
            .catch(err => console.error(err));
    } else {
        axios.post('/api/taxes', payload)
            .then(() => {
                this.$toasts.push({ type: 'success', message: 'Tax created' });
                this.getTaxes();
                this.closeTaxModal();
            })
            .catch(err => console.error(err));
    }
},


        toggleTaxEnabled(tax) {
            axios.put(`/api/taxes/${tax.id}`, { enabled: !!tax.enabled })
                .then(() => {
                    this.$toasts.push({ type: 'success', message: `${tax.name} ${tax.enabled ? 'enabled' : 'disabled'}` });
                })
                .catch(err => {
                    console.error(err);
                    tax.enabled = !tax.enabled; // revert on error
                });
        },

        confirmDeleteTax(t) {
            if (!confirm(`Delete tax "${t.name}"?`)) return;
            this.deleteTax(t.id);
        },

        deleteTax(id) {
            axios.delete(`/api/taxes/${id}`)
                .then(() => {
                    this.$toasts.push({ type: 'success', message: 'Tax deleted' });
                    this.getTaxes();
                })
                .catch(err => console.error(err));
        },

        /* -------------------- Dukakit Payments -------------------- */

        getDukakitPaymentsStatus() {
            return axios.get('/api/dukakit-payments/settings')
                .then(({ data }) => {
                    this.dukakitPayments = { ...this.dukakitPayments, ...data };
                })
                .catch(err => console.error('getDukakitPaymentsStatus', err));
        },

        toggleDukakitPayments() {
            const action = this.dukakitPayments.enabled ? 'enable' : 'disable';
            axios.post(`/api/dukakit-payments/settings/${action}`)
                .then(() => {
                    this.$toasts.push({ type: 'success', message: `${this.$platformBrand.name} Payments ${action}d` });
                })
                .catch(err => {
                    console.error(err);
                    this.dukakitPayments.enabled = !this.dukakitPayments.enabled;
                });
        },

        requestAccountNumber() {
            this.accountNumberSuggestions = [];
            axios.post('/api/dukakit-payments/settings/account-number', { preferred: this.accountNumberPreference || undefined })
                .then(({ data }) => {
                    this.dukakitPayments.account_number = data.account_number;
                    this.$toasts.push({ type: 'success', message: `Assigned ${this.$platformBrand.name} account ${data.account_number}` });
                })
                .catch(err => {
                    this.accountNumberSuggestions = err.response?.data?.suggestions || [];
                    this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Could not assign account number' });
                });
        },

        validateSettlementIdentifier(phoneNumber) {
            this.settlementIdentifierValidMessage = phoneNumber.valid ? '' : 'Enter a valid phone number';
        },

        setupSettlementAccount() {
            if (!this.settlementAccountForm.account_identifier) return;
            if (this.settlementAccountForm.destination_type === 'mpesa_number' && this.settlementIdentifierValidMessage) return;
            const needsAccountReference = ['paybill', 'till'].includes(this.settlementAccountForm.destination_type);
            if (needsAccountReference && !this.settlementAccountForm.account_reference) {
                this.$toasts.push({ type: 'error', message: `An account reference is required when settling to a ${this.settlementAccountForm.destination_type === 'till' ? 'Till' : 'Paybill'}` });
                return;
            }
            axios.post('/api/dukakit-payments/settings/settlement-account', { ...this.settlementAccountForm })
                .then(({ data }) => {
                    this.dukakitPayments.settlement_account = {
                        masked_identifier: data.masked_identifier,
                        destination_type: this.settlementAccountForm.destination_type,
                        locked: true,
                    };
                    this.settlementAccountForm = { destination_type: 'mpesa_number', account_identifier: '', account_reference: '' };
                    this.$toasts.push({ type: 'success', message: 'Settlement account saved and locked' });
                })
                .catch(err => this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Could not save settlement account' }));
        },

        saveSettlementFrequency() {
            axios.post('/api/dukakit-payments/settings/settlement-frequency', { frequency: this.dukakitPayments.settlement_frequency })
                .then(() => this.$toasts.push({ type: 'success', message: 'Settlement frequency updated' }))
                .catch(err => console.error(err));
        },

        settlementTypeLabel(type) {
            return { mpesa_number: 'Phone', paybill: 'Paybill', till: 'Till' }[type] || type;
        },

        /* -------------------- Existing settings fetchers + save -------------------- */

        getCompanySettings() {
            return axios.get('/api/settings')
                .then(({ data }) => {
                    this.paymentDetails.useDukakitShortcode = !!data.use_dukakit_paybill;
                    this.enableShifts = !!data.enable_shifts;
                    this.openingCashSettings.enable_opening_cash               = !!data.enable_opening_cash;
                    this.openingCashSettings.require_opening_cash_before_sales = !!data.require_opening_cash_before_sales;
                    this.openingCashSettings.enable_shift_cash_closing         = !!data.enable_shift_cash_closing;
                    this.paymentValidationSettings.enable_payment_validation          = !!data.enable_payment_validation;
                    this.paymentValidationSettings.allow_all_employees_skip_validation = !!data.allow_all_employees_skip_validation;
                    this.walletSettings.enable_customer_wallet = !!data.enable_customer_wallet;

                    this.etimsEnabled = !!data.enable_tims;
                    if (data.etims_submission_policy) {
                        this.etimsSubmissionSettings.etims_submission_policy = data.etims_submission_policy;
                    }
                    if (data.etims_submission_start_time) {
                        this.etimsSubmissionSettings.etims_submission_start_time = data.etims_submission_start_time.slice(0, 5);
                    }
                    if (data.etims_submission_end_time) {
                        this.etimsSubmissionSettings.etims_submission_end_time = data.etims_submission_end_time.slice(0, 5);
                    }
                    if (data.etims_submission_timezone) {
                        this.etimsSubmissionSettings.etims_submission_timezone = data.etims_submission_timezone;
                    }
                })
                .catch(err => console.error(err));
        },

        getMpesaConfigs() {
            return axios.get('/api/mpesa-configs')
                .then(({ data }) => {
                    this.paymentDetails.mpesaDetails.keys_unreadable = false;
                    Object.assign(this.paymentDetails.mpesaDetails, data || {});
                    if (!this.paymentDetails.mpesaDetails.account_type) this.paymentDetails.mpesaDetails.account_type = 'paybill';
                    // Not stored - show the key fields again when keys exist
                    if (data && (data.consumer_key || data.keys_unreadable)) {
                        this.paymentDetails.mpesaDetails.haveApiKeys = true;
                    }
                })
                .catch(err => console.error(err));
        },

        async registerC2bUrls() {
            const ok = await this.$bvModal.msgBoxConfirm(
                'This tells Safaricom to send payments made to your Paybill/Till to this system. It replaces any URLs already registered on that number. Make sure your number and keys are saved first.',
                { title: 'Register with Safaricom', okTitle: 'Register', okVariant: 'primary', cancelVariant: 'outline-secondary', centered: true }
            );
            if (!ok) return;
            this.registeringC2b = true;
            this.c2bRegisterResult = null;
            try {
                const { data } = await axios.post('/api/mpesa-register-c2b');
                this.c2bRegisterResult = { ok: true, message: data.message };
            } catch (e) {
                this.c2bRegisterResult = { ok: false, message: e.response?.data?.message || 'Could not reach Safaricom. Please try again.' };
            } finally {
                this.registeringC2b = false;
            }
        },

        copyText(text) {
            const done = () => this.$toasts.push({ type: 'success', message: 'Copied' });
            const failed = () => this.$toasts.push({ type: 'error', message: 'Could not copy - select and copy it manually' });

            // navigator.clipboard only exists on https/localhost
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(done).catch(failed);
                return;
            }

            const el = document.createElement('textarea');
            el.value = text;
            el.setAttribute('readonly', '');
            el.style.position = 'fixed';
            el.style.opacity = '0';
            document.body.appendChild(el);
            el.select();
            try {
                document.execCommand('copy') ? done() : failed();
            } catch (e) {
                failed();
            } finally {
                document.body.removeChild(el);
            }
        },

        saveSettingsDetails() {
            const saves = [
                axios.post('/api/settings/payments', { ...this.paymentDetails, ...this.walletSettings }),
                axios.post('/api/settings/opening-cash', { ...this.openingCashSettings }),
                axios.post('/api/settings/payment-validation', { ...this.paymentValidationSettings }),
            ];

            if (this.etimsEnabled) {
                saves.push(axios.post('/api/settings/etims-submission-policy', { ...this.etimsSubmissionSettings }));
            }

            Promise.all(saves)
                .then(() => {
                    this.$toasts.push({ type: 'success', message: 'Payment details have been updated' });
                    this.getMpesaConfigs();
                })
                .catch(err => {
                    const errors = err.response?.data?.errors;
                    this.$toasts.push({
                        type: 'error',
                        message: (errors && Object.values(errors)[0]?.[0]) || err.response?.data?.message || 'Could not save payment settings',
                    });
                });
        }
    }
};
</script>

<style scoped>
/* keep your minimalistic look */
.card { border: 0; }
.custom-control-label::before { cursor: pointer; }
.btn-sm { padding: .35rem .6rem; }
</style>
